import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { PASSWORD_MIN_LENGTH } from "@wr/contracts";
import { t, type StringKey } from "@wr/i18n";
import {
  AuthCard,
  Button,
  FormBanner,
  InlineMessage,
  PasswordField,
  StrengthMeter,
  scorePassword,
} from "@wr/ui";
import { resetPassword } from "../lib/auth";

/**
 * Reset password — design spec §8.2.
 *
 * ── Token validation runs before the first paint of the form. LOCKED. ───────
 *
 * Nobody types a password into a dead form. The validation is not an extra
 * request: the email link points at `GET /api/auth/reset-password/:token`,
 * which validates server-side and then redirects here with either `?token=…`
 * or `?error=…`. So by the time this component mounts the answer already
 * exists in the URL, and a missing token is treated as a dead one.
 *
 * **Accepted deviation (flagged in T-170's output).** Ivy specifies three
 * distinct dead-token strings — expired / used / invalid — and Theo wrote all
 * three. Better Auth returns a single `INVALID_TOKEN` for all three cases and
 * exposes no way to tell them apart, so `reset.invalid` is what renders today.
 * The other two keys are kept, not deleted: the layout is one layout, and the
 * day the token layer distinguishes them this is a one-line change.
 */
export function ResetScreen() {
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const token = params.get("token") ?? "";
  const urlError = params.get("error");

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [confirmTouched, setConfirmTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [failure, setFailure] = useState<StringKey | null>(null);

  const deadToken: StringKey | null = urlError
    ? deadTokenKey(urlError)
    : token
      ? null
      : "reset.invalid";
  const strength = scorePassword(password, PASSWORD_MIN_LENGTH);
  // `mismatch` shows on blur only — flagging a mismatch on the first keystroke
  // of the confirm field is technically true and practically hostile.
  const mismatch = confirmTouched && confirm.length > 0 && confirm !== password;
  const canSubmit = strength.acceptable && confirm === password && confirm.length > 0;

  async function submit() {
    setSubmitting(true);
    setFailure(null);
    try {
      await resetPassword(token, password);
      // §8.2 `success`: navigate to /login carrying a banner that survives
      // exactly one route change — hence router state, not a query param.
      void navigate("/login", { replace: true, state: { banner: t("reset.success") } });
    } catch {
      setFailure("reset.invalid");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="wr-auth-layout">
      <div className="wr-auth-layout__form">
        <div className="wr-form-column">
          <AuthCard testId="reset-card">
            <h1 className="wr-display-lg" data-route-heading>
              {t("reset.title")}
            </h1>

            {deadToken || failure ? (
              // The form is NOT rendered at all in this state (§8.2, LOCKED).
              <>
                <FormBanner tone="error" testId="reset-dead-token">
                  {t(deadToken ?? failure ?? "reset.invalid")}
                </FormBanner>
                <Button
                  variant="primary"
                  size="lg"
                  fullWidth
                  data-testid="reset-request-new"
                  onClick={() => void navigate("/forgot")}
                >
                  {t("reset.requestNew")}
                </Button>
              </>
            ) : (
              <form
                className="wr-stack"
                style={{ gap: 16 }}
                onSubmit={(event) => {
                  event.preventDefault();
                  if (canSubmit && !submitting) void submit();
                }}
              >
                <PasswordField
                  id="reset-password"
                  eyebrow={t("reset.newPassword")}
                  autoComplete="new-password"
                  value={password}
                  readOnly={submitting}
                  data-testid="reset-password"
                  revealLabel={t("auth.passwordReveal")}
                  hideLabel={t("auth.passwordHide")}
                  onChange={(event) => setPassword(event.target.value)}
                />

                <StrengthMeter
                  password={password}
                  minLength={PASSWORD_MIN_LENGTH}
                  ruleLabels={{
                    length: t("auth.strength.length", { min: PASSWORD_MIN_LENGTH }),
                    case: t("auth.strength.case"),
                    number: t("auth.strength.number"),
                  }}
                  // §8.2 `weak`: the primary is disabled WITH a reason. Never a
                  // silent dead button.
                  reason={
                    password.length > 0 && !strength.acceptable
                      ? t("auth.strength.tooWeak")
                      : undefined
                  }
                />

                <PasswordField
                  id="reset-confirm"
                  eyebrow={t("reset.confirmPassword")}
                  autoComplete="new-password"
                  value={confirm}
                  readOnly={submitting}
                  data-testid="reset-confirm"
                  revealLabel={t("auth.passwordReveal")}
                  hideLabel={t("auth.passwordHide")}
                  onBlur={() => setConfirmTouched(true)}
                  onChange={(event) => setConfirm(event.target.value)}
                />
                {mismatch ? (
                  <InlineMessage tone="error" id="reset-mismatch">
                    {t("reset.mismatch")}
                  </InlineMessage>
                ) : null}

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  fullWidth
                  loading={submitting}
                  loadingLabel={t("common.working")}
                  disabled={!canSubmit}
                  data-testid="reset-submit"
                >
                  {t("reset.submit")}
                </Button>
              </form>
            )}

            {/* Persistent in every state of both password screens (§8, LOCKED). */}
            <Link
              className="wr-btn wr-btn--ghost wr-btn--md wr-btn--full"
              to="/login"
              data-testid="reset-back"
            >
              {t("common.backToLogin")}
            </Link>
          </AuthCard>
        </div>
      </div>
    </div>
  );
}

/**
 * Better Auth sends one code for every dead token. The mapping is kept as a
 * function so that when the API can distinguish expiry from re-use, the other
 * two strings light up here and nowhere else.
 */
function deadTokenKey(code: string): StringKey {
  switch (code.toUpperCase()) {
    case "TOKEN_EXPIRED":
      return "reset.expired";
    case "TOKEN_USED":
      return "reset.used";
    default:
      return "reset.invalid";
  }
}
